import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const lang = () => (localStorage.getItem('appLanguage') === 'es' ? 'es' : 'en');

// Resolve a backend-relative image path to an absolute URL.
export const mediaUrl = (url) => (url && url.startsWith('/') ? `${API}${url}` : url);

export const postsApi = {
  uploadImage: async (blob, filename = 'upload.jpg') => {
    const form = new FormData();
    form.append('file', blob, filename);
    const { data } = await axios.post(`${API}/api/community/upload-image`, form, {
      ...cfg,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data; // { image_url, path }
  },
  create: async (payload) => (await axios.post(`${API}/api/community/posts`, payload, cfg)).data,
  remove: async (id) => (await axios.delete(`${API}/api/community/posts/${id}`, cfg)).data,
  update: async (id, caption) => (await axios.patch(`${API}/api/community/posts/${id}`, { caption }, cfg)).data,
  report: async (id, reason, note = '') => (await axios.post(`${API}/api/community/posts/${id}/report`, { reason, note }, cfg)).data,
  react: async (id, emoji) => (await axios.post(`${API}/api/community/posts/${id}/react`, { emoji }, cfg)).data,
  feed: async (mode = 'for_you') => (await axios.get(`${API}/api/community/posts/feed?mode=${mode}&language=${lang()}`, cfg)).data.posts,
  explore: async () => (await axios.get(`${API}/api/community/posts/explore?language=${lang()}`, cfg)).data.posts,
  suggestions: async (limit = 15) => (await axios.get(`${API}/api/community/suggestions?limit=${limit}`, cfg)).data.users,
  followMany: async (userIds) => (await axios.post(`${API}/api/community/follow-many`, { user_ids: userIds }, cfg)).data,
  testimonies: async () => (await axios.get(`${API}/api/community/posts/testimonies`, cfg)).data.posts,
  featured: async () => (await axios.get(`${API}/api/community/posts/featured-testimony`, cfg)).data.post,
  feature: async (id) => (await axios.post(`${API}/api/community/posts/${id}/feature`, {}, cfg)).data,
  unfeature: async () => (await axios.post(`${API}/api/community/posts/featured-testimony/clear`, {}, cfg)).data,
  userPosts: async (uid) => (await axios.get(`${API}/api/community/posts/user/${uid}?language=${lang()}`, cfg)).data.posts,
  like: async (id) => (await axios.post(`${API}/api/community/posts/${id}/like`, {}, cfg)).data,
  unlike: async (id) => (await axios.delete(`${API}/api/community/posts/${id}/like`, cfg)).data,
  comments: async (id) => (await axios.get(`${API}/api/community/posts/${id}/comments`, cfg)).data.comments,
  addComment: async (id, text) => (await axios.post(`${API}/api/community/posts/${id}/comments`, { text }, cfg)).data,
  profile: async (uid) => (await axios.get(`${API}/api/community/profile/${uid}`, cfg)).data.profile,
  follow: async (uid) => (await axios.post(`${API}/api/community/follow/${uid}`, {}, cfg)).data,
  unfollow: async (uid) => (await axios.delete(`${API}/api/community/follow/${uid}`, cfg)).data,
  // Usernames + mentions
  searchUsers: async (q) => (await axios.get(`${API}/api/community/search?q=${encodeURIComponent(q)}&limit=6`, cfg)).data.users,
  resolveUsername: async (username) => (await axios.get(`${API}/api/community/user/by-username?username=${encodeURIComponent(username)}`, cfg)).data,
  checkUsername: async (username) => (await axios.get(`${API}/api/auth/username/check?username=${encodeURIComponent(username)}`, cfg)).data,
  setUsername: async (username) => (await axios.put(`${API}/api/auth/username`, { username }, cfg)).data,
  // Notifications
  notifications: async () => (await axios.get(`${API}/api/community/notifications?unread_only=false`, cfg)).data.notifications,
  markNotificationsRead: async () => (await axios.post(`${API}/api/community/notifications/read`, {}, cfg)).data,
};

// Verse-image gradient presets (used by the create modal canvas renderer).
export const VERSE_GRADIENTS = [
  { id: 'dusk', from: '#4f46e5', to: '#0ea5e9', name: 'Dusk' },
  { id: 'ember', from: '#b45309', to: '#e11d48', name: 'Ember' },
  { id: 'forest', from: '#065f46', to: '#0d9488', name: 'Forest' },
  { id: 'royal', from: '#6d28d9', to: '#db2777', name: 'Royal' },
  { id: 'night', from: '#0f172a', to: '#334155', name: 'Night' },
];

// Render a verse-image card to a PNG blob using canvas (no external deps).
export const renderVerseImage = (verseText, reference, gradient) =>
  new Promise((resolve) => {
    const size = 1080;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, size, size);
    g.addColorStop(0, gradient.from); g.addColorStop(1, gradient.to);
    ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
    // subtle vignette
    ctx.fillStyle = 'rgba(0,0,0,0.18)'; ctx.fillRect(0, 0, size, size);

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.font = '600 52px Georgia, serif';
    const maxW = size - 200;
    const words = (verseText || '').trim().split(/\s+/);
    const lines = [];
    let line = '';
    words.forEach((w) => {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w; }
      else line = test;
    });
    if (line) lines.push(line);
    const lh = 70;
    let y = size / 2 - (lines.length * lh) / 2;
    lines.slice(0, 9).forEach((ln) => { ctx.fillText(ln, size / 2, y); y += lh; });

    ctx.font = '700 40px Georgia, serif';
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillText((reference || '').toUpperCase(), size / 2, y + 50);

    ctx.font = '500 26px Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.fillText('Lumera', size / 2, size - 60);

    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });

// Render a keepsake streak-milestone badge story card to a PNG blob.
export const renderBadgeImage = (days, kind = 'personal', partnerName = '', es = false) =>
  new Promise((resolve) => {
    const size = 1080;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    const shared = kind === 'partner';
    const g = ctx.createLinearGradient(0, 0, size, size);
    if (shared) { g.addColorStop(0, '#9d174d'); g.addColorStop(1, '#6d28d9'); }
    else { g.addColorStop(0, '#b45309'); g.addColorStop(1, '#f59e0b'); }
    ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = 'rgba(0,0,0,0.10)'; ctx.fillRect(0, 0, size, size);

    ctx.textAlign = 'center';
    // Medallion ring
    ctx.beginPath();
    ctx.arc(size / 2, 420, 210, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.16)'; ctx.fill();
    ctx.lineWidth = 10; ctx.strokeStyle = 'rgba(255,255,255,0.55)'; ctx.stroke();

    ctx.font = '120px Arial';
    ctx.fillText(shared ? '🤝' : '🔥', size / 2, 400);
    ctx.fillStyle = '#ffffff';
    ctx.font = '800 130px Georgia, serif';
    ctx.fillText(String(days), size / 2, 560);
    ctx.font = '600 40px Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillText((es ? 'DÍAS' : 'DAYS'), size / 2, 470);

    ctx.font = '700 56px Georgia, serif';
    ctx.fillStyle = '#ffffff';
    const title = shared
      ? (es ? 'Racha de oración compartida' : 'Shared prayer streak')
      : (es ? 'Racha de oración' : 'Prayer streak');
    ctx.fillText(title, size / 2, 720);

    if (shared && partnerName) {
      ctx.font = '500 38px Arial, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillText((es ? 'con ' : 'with ') + partnerName, size / 2, 780);
    }

    ctx.font = '600 34px Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fillText(es ? '¡Dios es fiel! 🙌' : 'God is faithful! 🙌', size / 2, size - 120);
    ctx.font = '500 28px Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.fillText('Lumera', size / 2, size - 60);

    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });

export const renderPulseRecapImage = (pulse, es = false) =>
  new Promise((resolve) => {
    const size = 1080;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, size, size);
    g.addColorStop(0, '#6d28d9'); g.addColorStop(0.55, '#a21caf'); g.addColorStop(1, '#e11d48');
    ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = 'rgba(0,0,0,0.12)'; ctx.fillRect(0, 0, size, size);

    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.font = '600 34px Arial, sans-serif';
    ctx.fillText((es ? 'ESTA SEMANA EN LA COMUNIDAD' : 'THIS WEEK IN COMMUNITY').toUpperCase(), size / 2, 150);

    ctx.fillStyle = '#ffffff';
    ctx.font = '800 96px Georgia, serif';
    ctx.fillText('Lumera', size / 2, 260);

    const top = (pulse.top_streaks || [])[0];
    const rows = [
      { n: String(pulse.new_testimonies ?? 0), label: es ? 'testimonios nuevos' : 'new testimonies' },
      { n: String(pulse.answered_prayers ?? 0), label: es ? 'oraciones respondidas' : 'answered prayers' },
    ];
    if (top) rows.push({ n: `🔥 ${top.current_streak}`, label: es ? 'mejor racha de oración' : 'top prayer streak' });

    let y = 430;
    const cardW = size - 220; const cardX = 110; const cardH = 150; const gap = 34;
    rows.forEach((r) => {
      ctx.fillStyle = 'rgba(255,255,255,0.14)';
      const rr = 28;
      ctx.beginPath();
      ctx.moveTo(cardX + rr, y);
      ctx.arcTo(cardX + cardW, y, cardX + cardW, y + cardH, rr);
      ctx.arcTo(cardX + cardW, y + cardH, cardX, y + cardH, rr);
      ctx.arcTo(cardX, y + cardH, cardX, y, rr);
      ctx.arcTo(cardX, y, cardX + cardW, y, rr);
      ctx.closePath(); ctx.fill();
      ctx.textAlign = 'left';
      ctx.fillStyle = '#ffffff';
      ctx.font = '800 78px Georgia, serif';
      ctx.fillText(r.n, cardX + 50, y + cardH / 2 + 12);
      ctx.textAlign = 'right';
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = '600 38px Arial, sans-serif';
      ctx.fillText(r.label, cardX + cardW - 50, y + cardH / 2 + 12);
      y += cardH + gap;
    });

    ctx.textAlign = 'center';
    ctx.font = '600 30px Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillText(es ? 'Únete a la comunidad Lumera 🙌' : 'Join the Lumera community 🙌', size / 2, size - 70);

    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
